// Browser audio diagnostics for the "Test Audio" modal: mic/speaker checks, human-readable issues, and a level meter.

// Twilio Voice SDK's default incoming-call ringtone, so a successful test previews the real call sound.
export const TWILIO_INCOMING_SOUND_URL = 'https://sdk.twilio.com/js/client/sounds/releases/1.0.0/incoming.mp3';

export type AudioIssueCode =
  | 'insecure_context'
  | 'unsupported_browser'
  | 'no_microphone'
  | 'permission_denied'
  | 'permission_dismissed'
  | 'microphone_busy'
  | 'microphone_error'
  | 'no_speakers'
  | 'no_signal';

export interface AudioIssue {
  code: AudioIssueCode;
  message: string;
}

export const ISSUE_MESSAGES: Record<AudioIssueCode, string> = {
  insecure_context: 'Microphone access requires a secure (https) connection. Please open Avochato over https.',
  unsupported_browser: 'This browser does not support microphone access. Please use a recent version of Chrome, Edge, Firefox or Safari.',
  no_microphone: 'No microphone detected. Please connect a microphone or headset and try again.',
  // Generic fallback; issueFromMediaError swaps in browser-specific unblocking
  // instructions via permissionDeniedMessage() when it recognises the browser.
  permission_denied: 'Microphone access is blocked. Allow microphone access for this site in your browser settings, then reload the page.',
  permission_dismissed: 'Microphone permission was not granted. Please allow microphone access when prompted.',
  microphone_busy: 'The microphone could not be started. It may be in use by another application, or disabled in your system settings.',
  microphone_error: 'Something went wrong while accessing the microphone. Please check your device settings and try again.',
  no_speakers: 'No audio output device detected. Please connect speakers or a headset.',
  no_signal: 'No sound is being picked up by the microphone. Make sure it is not muted and that the correct input device is selected in your system settings.',
};

const issue = (code: AudioIssueCode): AudioIssue => ({ code, message: ISSUE_MESSAGES[code] });

export type BrowserFamily = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

// Coarse user-agent sniffing, used only to word permission instructions. Order
// matters: Edge UAs contain "Chrome" and "Safari", Chrome UAs contain "Safari".
export const detectBrowser = (ua: string): BrowserFamily => {
  if (/Edg(e|A|iOS)?\//.test(ua)) return 'edge';
  if (/(Firefox|FxiOS)\//.test(ua)) return 'firefox';
  if (/(Chrome|CriOS)\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
};

// Each browser hides the per-site microphone toggle somewhere different, so the
// "how to unblock" instructions have to be browser-specific to be actionable.
const PERMISSION_DENIED_MESSAGES: Record<BrowserFamily, string> = {
  chrome: 'Microphone access is blocked. Click the icon to the left of the address bar (a lock or sliders icon), turn on Microphone, then reload the page.',
  edge: 'Microphone access is blocked. Click the lock icon in the address bar, set Microphone to Allow, then reload the page.',
  firefox: 'Microphone access is blocked. Click the crossed-out microphone icon in the address bar, remove the block, then reload the page and choose Allow when prompted.',
  safari: 'Microphone access is blocked. In the Safari menu, choose "Settings for This Website" (or Safari Settings, then Websites, then Microphone), set this site to Allow, then reload the page.',
  other: ISSUE_MESSAGES.permission_denied,
};

export const permissionDeniedMessage = (
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): string => PERMISSION_DENIED_MESSAGES[detectBrowser(ua)];

export const isMicrophoneSupported = (nav: Navigator = navigator) => (
  typeof nav.mediaDevices?.getUserMedia === 'function'
);

// Maps a getUserMedia rejection onto an issue; browser error names aren't fully consistent, so this errs toward a useful message.
export const issueFromMediaError = (error: unknown, hasMicrophone: boolean): AudioIssue => {
  const name = (error as { name?: string })?.name ?? '';
  switch (name) {
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return issue('no_microphone');
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      // Chrome uses this for both "Block" and dismissing the prompt, so we can only offer generic guidance.
      return { code: 'permission_denied', message: permissionDeniedMessage() };
    case 'PermissionDismissedError':
    case 'AbortError':
      return issue('permission_dismissed');
    case 'NotReadableError':
    case 'TrackStartError':
      return issue(hasMicrophone ? 'microphone_busy' : 'no_microphone');
    default:
      return issue(hasMicrophone ? 'microphone_error' : 'no_microphone');
  }
};

export interface DeviceSummary {
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  // Whether the browser enumerates `audiooutput` at all (Firefox/Safari don't), so `outputs` is only meaningful when true.
  outputsSupported: boolean;
}

export const summarizeDevices = async (nav: Navigator = navigator): Promise<DeviceSummary> => {
  if (typeof nav.mediaDevices?.enumerateDevices !== 'function') {
    return { inputs: [], outputs: [], outputsSupported: false };
  }
  const devices = await nav.mediaDevices.enumerateDevices();
  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const outputs = devices.filter((d) => d.kind === 'audiooutput');
  // Chromium exposes `setSinkId`, and along with it a list of outputs.
  const outputsSupported = typeof (HTMLMediaElement.prototype as any).setSinkId === 'function';
  return { inputs, outputs, outputsSupported };
};

export interface MicrophoneCheck {
  stream: MediaStream | null;
  issues: AudioIssue[];
  devices: DeviceSummary;
}

// Runs pre-flight checks and, if they pass, requests microphone access; the returned stream must be stopped by the caller.
export const checkMicrophone = async (
  nav: Navigator = navigator,
  secure: boolean = typeof window !== 'undefined' ? window.isSecureContext : true,
): Promise<MicrophoneCheck> => {
  const empty: DeviceSummary = { inputs: [], outputs: [], outputsSupported: false };

  if (!isMicrophoneSupported(nav)) {
    return { stream: null, issues: [issue(secure ? 'unsupported_browser' : 'insecure_context')], devices: empty };
  }
  if (!secure) {
    return { stream: null, issues: [issue('insecure_context')], devices: empty };
  }

  // Ask for the mic straight away: some browsers won't enumerate devices until permission is granted, so checking first would false-negative.
  let stream: MediaStream;
  try {
    stream = await nav.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    const devices = await summarizeDevices(nav).catch(() => empty);
    return { stream: null, issues: [issueFromMediaError(error, devices.inputs.length > 0)], devices };
  }

  // With permission granted the device list is complete and labelled.
  const devices = await summarizeDevices(nav).catch(() => empty);

  const issues: AudioIssue[] = [];
  if (devices.outputsSupported && devices.outputs.length === 0) {
    issues.push(issue('no_speakers'));
  }
  return { stream, issues, devices };
};

export const stopStream = (stream: MediaStream | null | undefined) => {
  stream?.getTracks().forEach((track) => track.stop());
};

export interface LevelMeter {
  // Current input level, 0..1, scaled so normal speech sits comfortably mid-bar.
  read(): number;
  stop(): Promise<void>;
}

const AudioContextCtor = () => (
  typeof window !== 'undefined'
    ? (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined
    : undefined
);

export const isLevelMeterSupported = () => Boolean(AudioContextCtor());

// Wires the stream through an AnalyserNode for polling; gain=4 makes typical speech RMS (0.05..0.2) reach a full bar.
export const createLevelMeter = (stream: MediaStream, gain = 4): LevelMeter => {
  const Ctor = AudioContextCtor();
  if (!Ctor) throw new Error('AudioContext is not supported in this browser');

  const context = new Ctor();
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const buffer = new Uint8Array(analyser.fftSize);

  // Browsers may start the context suspended until a user gesture; opening this modal via a click satisfies that.
  if (context.state === 'suspended') context.resume().catch(() => {});

  return {
    read() {
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        const v = (buffer[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buffer.length);
      return Math.min(1, rms * gain);
    },
    async stop() {
      source.disconnect();
      analyser.disconnect();
      if (context.state !== 'closed') await context.close().catch(() => {});
    },
  };
};
